/**
 * Типы задач. Модуль для работы с API
 * @module taskTypes
 */

import { maxPerPage } from "@/constants";

/**
 * Получение списка типов задач
 * @param {Object} params - Параметры запроса
 * @returns {Promise<Object>} - список типов задач
 * @throws {Error} - ошибка при получении данных
 */
export const getList = async (params) => {
  const queryString = new URLSearchParams(params).toString();

  try {
    const response = await fetch(`/api/tasktypes?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение списка типов задач без пагинации
 * @param {Object} params - Параметры запроса
 * @returns {Promise<Object>} -список типов задач
 * @throws {Error} - ошибка при получении данных
 */
export const getTaskTypeOptions = async (params) => {
  const queryString = new URLSearchParams({
    ...params,
    page: 1,
    perpage: maxPerPage,
  }).toString();

  try {
    const response = await fetch(`/api/tasktypes?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.items;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение типа задачи по uuid
 * @param {string} uuid - UUID типа задачи
 * @returns {Promise<Object>} - данные типа задачи
 * @throws {Error} - ошибка при получении данных
 */
export const getItem = async (uuid) => {
  try {
    const response = await fetch(`/api/tasktype?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};

/**
 * Создание типа задачи
 * @param {Object} item - данные типа задачи
 * @returns {Promise<Object>} - данные созданного типа задачи
 * @throws {Error} - ошибка при создании данных
 */
export const createItem = async (item) => {
  try {
    const response = await fetch("/api/tasktype", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при создании данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление типа задачи
 * @param {Object} item - данные типа задачи
 * @returns {Promise<Object>} - обновленные данные типа задачи
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateItem = async (item) => {
  try {
    const response = await fetch("/api/tasktype", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление типа задачи
 * @param {Object} item - данные типа задачи
 * @returns {Promise<Object>} - удаленные данные типа задачи
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteItem = async (item) => {
  try {
    const response = await fetch("/api/tasktype", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`
      );
    }

    return data.item;
  } catch (err) {
    throw err;
  }
};
